import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { FormSimpleAutoComplete } from '../index';
import { ListParams } from '../../types/main';
import { Option, SelectProps } from './types';
import ReactFormSimpleAutoComplete from '../form-simple-autocomplete/ReactFormSimpleAutoComplete';
import { filtersToObject } from './util';

type Props = SelectProps & {
  searchOptions: (params: ListParams) => Promise<Option[]>;
};

const BaseSearchSelect = ({
  disabled = false,
  name,
  label,
  filters = [],
  firstCallFilters = [],
  emptyValue = -1,
  searchValue = '',
  formType = 'formik',
  disableClearable = false,
  dependentFieldName = '',
  defaultParams = {},
  searchOptions,
  isRequired = false,
  selectFirstOption = false,
  callback,
  setOption,
}: Props) => {
  const dispatch = useDispatch();
  const [options, setOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);
  const { watch } = useFormContext();
  const dependentFieldValue = watch(dependentFieldName);
  const [defaultOption, setDefaultOption] = useState();
  const [initValue] = useState(watch(name));

  const getOptions = useCallback(async (listParams) => {
    setLoading(true);

    const opts = await searchOptions({
      ...listParams,
      filter: { ...listParams.filter, ...filtersToObject(filters) },
    });

    setOptions(opts);
    setLoading(false);
  }, [setOptions, setLoading, filters, searchOptions]);

  const searchOptionsHandler = useCallback((search) => {
    getOptions({ ...defaultParams, filter: { search } });
  }, [dispatch, getOptions]);

  // Init call
  /* eslint react-hooks/exhaustive-deps: 0 */
  useEffect(() => {
    if (setOption && setOption.label && setOption.id) {
      return;
    }

    getOptions({ ...defaultParams, filter: filtersToObject(firstCallFilters, searchValue) });
  }, [dispatch]); // firstCallFilters

  useEffect(() => {
    if (dependentFieldValue) {
      getOptions({});
    }
  }, [dependentFieldValue]);

  useEffect(() => {
    const option = options.find((opt) => initValue === opt.id);

    if (!defaultOption && option) {
      setDefaultOption(option);
    }
  }, [defaultOption, setDefaultOption, options]);

  useEffect(() => {
    if (setOption && setOption.id && setOption.label && !options.some((opt) => opt.id === setOption.id)) {
      setOptions([setOption]);
    }
  }, [setOption]);

  if (formType === 'react') {
    return (
      <ReactFormSimpleAutoComplete
        name={name}
        label={label}
        options={options}
        onSearch={searchOptionsHandler}
        loading={loading}
        disabled={disabled}
        emptyValue={emptyValue}
        disableClearable={disableClearable}
        dependentFieldName={dependentFieldName}
        dependentDefaultOption={defaultOption}
        isRequired={isRequired}
        selectFirstOption={selectFirstOption}
        callback={callback}
        setOption={setOption}
      />
    );
  }

  return (
    <FormSimpleAutoComplete
      name={name}
      label={label}
      options={options}
      onSearch={searchOptionsHandler}
      loading={loading}
      disabled={disabled}
    />
  );
};


export default BaseSearchSelect;
